/**
 * Limite do título em botões de resposta rápida no WhatsApp (Meta Cloud API).
 * Deve coincidir com WHATSAPP_REPLY_BUTTON_TITLE_MAX na API (uraButtons.js).
 */
import {
  isHttpUrl,
  parseChatbotButtonEntries,
  partitionChatbotButtonEntries,
  splitStoredButton,
} from "../../utils/chatbotButtonLinks";

export const WHATSAPP_REPLY_BUTTON_TITLE_MAX = 20;
/** Botões de resposta rápida nativos no WhatsApp (interactive reply). */
export const WHATSAPP_MAX_REPLY_BUTTONS = 3;
/** Opções em lista interativa no WhatsApp (Meta Cloud / Gupshup / API Fácil). */
export const WHATSAPP_MAX_LIST_ROWS = 10;
/** Botões CTA URL nativos no WhatsApp: no máximo 1 por mensagem/passo. */
export const WHATSAPP_MAX_LINK_BUTTONS = 1;

function entriesFromFormBotoes(botoes) {
  const arr = Array.isArray(botoes) ? botoes : [];
  const normalized = arr.map((item) => {
    if (item && typeof item === "object" && !Array.isArray(item)) {
      const url = String(item.url ?? "").trim();
      const label = String(item.label ?? "").trim();
      if (item.isLink || url) {
        if (url && isHttpUrl(url)) {
          if (!label || label === defaultLinkLabelForSave(url)) return url;
          return `${label}|${url}`;
        }
        return label;
      }
      return label;
    }
    return String(item ?? "").trim();
  });
  return parseChatbotButtonEntries(normalized);
}

/**
 * Indica como o WhatsApp enviará as opções de resposta (espelha whatsappInteractivePlan.js na API).
 * @returns {"reply"|"list"|"text"|null}
 */
export function getChatbotButtonWhatsAppDeliveryMode(botoes) {
  const { replies } = partitionChatbotButtonEntries(entriesFromFormBotoes(botoes));
  if (!replies.length) return null;
  if (replies.length <= WHATSAPP_MAX_REPLY_BUTTONS) return "reply";
  if (replies.length <= WHATSAPP_MAX_LIST_ROWS) return "list";
  return "text";
}

/**
 * @param {string[]|{label:string,url?:string,isLink?:boolean}[]} botoes
 * @returns {string|null} mensagem de erro ou null
 */
export function validateChatbotButtonLabels(botoes) {
  const arr = Array.isArray(botoes) ? botoes : [];
  const max = WHATSAPP_REPLY_BUTTON_TITLE_MAX;
  const { replies, links } = partitionChatbotButtonEntries(entriesFromFormBotoes(botoes));
  if (replies.length > WHATSAPP_MAX_LIST_ROWS) {
    return `Máximo de ${WHATSAPP_MAX_LIST_ROWS} opções de resposta por passo (limite do WhatsApp em listas).`;
  }
  if (links.length > WHATSAPP_MAX_LINK_BUTTONS) {
    return `Máximo de ${WHATSAPP_MAX_LINK_BUTTONS} botão com link por passo (limite do WhatsApp).`;
  }

  for (let i = 0; i < arr.length; i += 1) {
    const item = arr[i];
    let label = "";
    let url = "";
    let isLink = false;

    if (item && typeof item === "object" && !Array.isArray(item)) {
      label = String(item.label ?? "").trim();
      url = String(item.url ?? "").trim();
      isLink = Boolean(item.isLink || url);
    } else {
      const split = splitStoredButton(item);
      label = split.label;
      url = split.url;
      isLink = split.isLink;
    }

    if (!label && !url) continue;

    if (isLink || url) {
      if (!isHttpUrl(url)) {
        return `Botão ${i + 1}: informe uma URL válida (http:// ou https://).`;
      }
      if (!label) {
        return `Botão ${i + 1}: informe o rótulo do link.`;
      }
      if (label.length > max) {
        return `Botão ${i + 1}: rótulo do link com no máximo ${max} caracteres (WhatsApp).`;
      }
      continue;
    }

    if (label.length > max) {
      return `Botão ${i + 1}: máximo ${max} caracteres (WhatsApp).`;
    }
  }
  return null;
}

export function normalizeChatbotButtonsForSave(botoes) {
  const arr = Array.isArray(botoes) ? botoes : [];
  return arr
    .map((item) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        const url = String(item.url ?? "").trim();
        const label = String(item.label ?? "").trim();
        if (url && isHttpUrl(url)) {
          const clipped =
            label.length > WHATSAPP_REPLY_BUTTON_TITLE_MAX
              ? label.slice(0, WHATSAPP_REPLY_BUTTON_TITLE_MAX)
              : label;
          if (!clipped || clipped === defaultLinkLabelForSave(url)) return url;
          return `${clipped}|${url}`;
        }
        return label;
      }
      return String(item ?? "").trim();
    })
    .filter(Boolean);
}

function defaultLinkLabelForSave(url) {
  try {
    return new URL(url).hostname.replace(/^www\./i, "").slice(0, WHATSAPP_REPLY_BUTTON_TITLE_MAX);
  } catch (_) {
    return "Abrir link";
  }
}

export function parseChatbotButtonsForDisplay(raw) {
  return parseChatbotButtonEntries(raw);
}
